import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Sparkles } from "lucide-react";
import { userReportsApi } from "../../api";
import { Toggle } from "../ui";
import type { UserDashboard } from "../../types/reports";

interface NewDashboardModalProps {
  onClose: () => void;
  onCreated: (dashboard: UserDashboard) => void;
  workspaceId?: string | null;
}

export default function NewDashboardModal({ onClose, onCreated, workspaceId }: NewDashboardModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [shareToLibrary, setShareToLibrary] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !description.trim()) return;
    setLoading(true);
    try {
      const res = await userReportsApi.generateDashboard(name.trim(), description.trim());
      let dashboard = res.data;
      // Assign workspace and/or share to library in a single update if needed
      if (workspaceId || shareToLibrary) {
        const updated = await userReportsApi.updateDashboard(dashboard.id, {
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
          ...(shareToLibrary ? { is_shared: true } : {}),
        });
        dashboard = updated.data;
      }
      onCreated(dashboard);
    } catch { return; } finally {
      setLoading(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#1D345F]" />
            <h2 className="text-sm font-semibold text-gray-900">New dashboard</h2>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Dashboard name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekly Trading, Casino Performance…"
              maxLength={80}
              required
              disabled={loading}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#1D345F] focus:outline-none focus:ring-1 focus:ring-[#1D345F] disabled:bg-gray-50"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              What do you want to track?
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what matters to you — e.g. 'Daily GGR by sport, top markets by turnover, and player deposit trends this week'"
              rows={4}
              maxLength={2000}
              required
              disabled={loading}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#1D345F] focus:outline-none focus:ring-1 focus:ring-[#1D345F] disabled:bg-gray-50"
            />
          </div>

          <div className="flex items-center gap-3">
            <Toggle checked={shareToLibrary} onChange={setShareToLibrary} />
            <span className="text-sm text-gray-700">Share to Library</span>
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim() || !description.trim()}
              className="flex items-center gap-2 rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50"
            >
              {loading ? (
                <>
                  <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Generating…
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Create dashboard
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
